// File: frontend/src/pages/CategoryManagement.tsx
// Refactored with mobile-first design and unified add/edit form

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FolderTree, 
  Plus, 
  Download,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  useCategories, 
  useParentCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useToggleCategoryStatus
} from '@/hooks/useCategories';
import { CategoryFilters as CategoryFiltersType, CategoryFormData } from '@/lib/api/categoryService';
import {
  CategoryStats,
  CategoryTable,
  CategoryForm
} from '@/components/Category';
import { PageHeader, PageHeaderActions, LoadingState, ErrorState, PageFilters, useFilters, FilterPanel } from '@/components/common/ui';
import type { FilterField } from '@/components/common/ui/PageFilters';

type ViewMode = 'list' | 'form';

const CategoryManagement: React.FC = () => {
  const navigate = useNavigate();
  
  // State management
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Filter state using useFilters hook
  const {
    filters: filterValues,
    updateFilter,
    clearFilters: clearFilterValues,
    hasActiveFilters
  } = useFilters({
    search: '',
    parent: '',
    status: ''
  });

  // Memoize filters to prevent unnecessary re-renders
  const filters = useMemo<CategoryFiltersType>(() => ({
    page,
    limit,
    ...(filterValues.search && { search: filterValues.search }),
    ...(filterValues.parent && { parent: filterValues.parent }),
    ...(filterValues.status && { status: filterValues.status })
  }), [page, limit, filterValues.search, filterValues.parent, filterValues.status]);

  // React Query hooks
  const { 
    data: categoriesData, 
    isLoading, 
    error, 
    refetch 
  } = useCategories(filters);

  const { 
    data: parentCategories, 
    isLoading: isLoadingParents 
  } = useParentCategories();

  // Mutation hooks
  const createCategoryMutation = useCreateCategory();
  const updateCategoryMutation = useUpdateCategory();
  const deleteCategoryMutation = useDeleteCategory();
  const toggleStatusMutation = useToggleCategoryStatus();

  // Filter fields configuration
  const filterFields = useMemo<FilterField[]>(() => [
    {
      key: 'search',
      label: 'Search Categories',
      type: 'search',
      placeholder: 'Search by category name or description...',
      width: 'lg',
      clearable: true
    },
    {
      key: 'parent',
      label: 'Parent Category',
      type: 'select',
      width: 'md',
      options: [
        { label: 'Top Level Only', value: 'null' },
        ...(parentCategories || []).map(parent => ({
          label: parent.name,
          value: parent.category_id.toString()
        }))
      ]
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      width: 'md',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' }
      ]
    }
  ], [parentCategories]);

  // Event handlers
  const handleFilterChange = useCallback((key: string, value: any) => {
    updateFilter(key, value);
    setPage(1);
  }, [updateFilter]);

  const handleClearFilters = useCallback(() => {
    clearFilterValues();
    setPage(1);
  }, [clearFilterValues]);

  const handleCreateNew = useCallback(() => {
    setEditingCategory(null);
    setViewMode('form');
  }, []);

  const handleEdit = useCallback((category: any) => {
    setEditingCategory(category);
    setViewMode('form');
  }, []);

  const handleCancel = useCallback(() => {
    setEditingCategory(null);
    setViewMode('list');
  }, []);

  const handleSubmit = useCallback(async (formData: CategoryFormData) => {
    if (editingCategory) {
      await updateCategoryMutation.mutateAsync({
        id: editingCategory.category_id,
        data: formData
      });
      toast.success('Category updated successfully');
    } else {
      await createCategoryMutation.mutateAsync(formData);
      toast.success('Category created successfully');
    }
    
    setEditingCategory(null);
    setViewMode('list');
  }, [editingCategory, createCategoryMutation, updateCategoryMutation]);

  const handleDelete = useCallback(async (categoryId: number, categoryName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${categoryName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteCategoryMutation.mutateAsync(categoryId);
      toast.success('Category deleted successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete category');
    }
  }, [deleteCategoryMutation]);

  const handleToggleStatus = useCallback(async (categoryId: number) => {
    try {
      await toggleStatusMutation.mutateAsync(categoryId);
      toast.success('Category status updated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update category status');
    }
  }, [toggleStatusMutation]);

  // Loading state
  if (isLoading && !categoriesData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Category Management"
          subtitle="Manage service categories and subcategories"
          icon={FolderTree}
        />
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <LoadingState
            variant="skeleton"
            title="Loading categories..."
            message="Please wait while we fetch your categories"
            size="md"
          />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Category Management"
          subtitle="Manage service categories and subcategories"
          icon={FolderTree}
        />
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <ErrorState
            type="server"
            title="Failed to load categories"
            message={error.message || 'Something went wrong. Please try again.'}
            onRetry={() => refetch()}
            size="md"
          />
        </div>
      </div>
    );
  }

  const isFormLoading = createCategoryMutation.isPending || updateCategoryMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* List View */}
      {viewMode === 'list' && (
        <>
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            {/* Simple Page Title - Just text, no card */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Category Management</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage service categories and subcategories
              </p>
            </div>
            {/* Stats as Info Section - No card wrapper */}
            {categoriesData?.stats && (
              <div className="mb-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Overview</h2>
                <CategoryStats 
                  stats={categoriesData.stats} 
                  isLoading={isLoading} 
                />
              </div>
            )}

            {/* Filters Section - Outside card */}
            <div className="mb-6">
              <FilterPanel
                fields={filterFields}
                values={filterValues}
                onChange={handleFilterChange}
                onClear={handleClearFilters}
                hasActiveFilters={hasActiveFilters}
                variant="default"
                title="Filter Categories"
                defaultExpanded={hasActiveFilters}
              />
            </div>

            {/* Actions - Below filters, above listing */}
            <div className="mb-6">
              <PageHeaderActions
                primaryAction={{
                  label: "Add Category",
                  icon: Plus,
                  onClick: handleCreateNew,
                }}
                secondaryActions={[
                  {
                    label: "Refresh",
                    icon: RefreshCw,
                    onClick: () => refetch(),
                    variant: "outline",
                    loading: isLoading,
                  },
                ]}
              />
            </div>

            {/* Mobile: No card wrapper for max space */}
            <div className="block xl:hidden">
              {/* Mobile Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Categories</h3>
                {categoriesData && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {categoriesData.pagination?.total || categoriesData.categories?.length || 0} total
                  </span>
                )}
              </div>
              <CategoryTable
                categories={categoriesData?.categories || []}
                isLoading={isLoading}
                searchTerm={filterValues.search}
                parentFilter={filterValues.parent}
                statusFilter={filterValues.status}
                pagination={categoriesData?.pagination}
                currentPage={page}
                onPageChange={setPage}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                onEdit={handleEdit}
                onCreateCategory={handleCreateNew}
                isDeleting={deleteCategoryMutation.isPending}
                isTogglingStatus={toggleStatusMutation.isPending}
              />
            </div>

            {/* Desktop: Card wrapper for clean look */}
            <div className="hidden xl:block bg-white rounded-lg border border-gray-200 shadow-sm">
              {/* Card Header with count */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Categories</h3>
                  {categoriesData && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {categoriesData.pagination?.total || categoriesData.categories?.length || 0} total
                    </span>
                  )}
                </div>
              </div>

              {/* Categories Listing */}
              <div className="p-6">
                <CategoryTable
                  categories={categoriesData?.categories || []}
                  isLoading={isLoading}
                  searchTerm={filterValues.search}
                  parentFilter={filterValues.parent}
                  statusFilter={filterValues.status}
                  pagination={categoriesData?.pagination}
                  currentPage={page}
                  onPageChange={setPage}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                  onEdit={handleEdit}
                  onCreateCategory={handleCreateNew}
                  isDeleting={deleteCategoryMutation.isPending}
                  isTogglingStatus={toggleStatusMutation.isPending}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Form View */}
      {viewMode === 'form' && (
        <>
          <PageHeader
            title={editingCategory ? 'Edit Category' : 'Create New Category'}
            subtitle={editingCategory 
              ? 'Update category information and settings' 
              : 'Add a new category to organize your services'}
            icon={FolderTree}
            secondaryActions={[
              {
                label: "Back to List",
                icon: ArrowLeft,
                onClick: handleCancel,
                variant: "outline",
              },
            ]}
          />

          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <CategoryForm
              category={editingCategory}
              parentCategories={parentCategories || []}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSubmitting={isFormLoading}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryManagement;